// Transaction Wizard — the operations (docs/plans/transaction-wizard.md §6.13)
// over the host's screen runtime: set → item_change, add_line → row_add,
// complete_line → row_complete, match_line, lookup, fetch_lines / recalc_lines,
// run_action, validate / validate_tree, fetch_url → wizard_fetch, goto, message —
// plus loading, locating and saving the frame's record.
//
// Every call goes through `host.screenRuntime(via)`, whose requests carry the
// wizard's `via` so the host authorizes them against the WIZARD's grant. Values
// land on the record exactly as the transaction screen lands them: the same
// ItemChangeResult contract (updates / forms / protect), the same row-change
// helpers (detailsBuffer), the same warnings / reason / confirmation loops.
//
// Each operation takes a frame and returns a NEW frame — the runner commits it.

import type { TxnScreenRuntime, BatchSaveBody, BatchSaveResult } from '../txn/screen/contract'
import { executeValidate } from '../txn/screen/contract'
import {
  applyFormRowsChange, applyFormsToChildren, childrenForEvent, formChangeTouchesRows,
  listAllChildRows, markChildrenSaved, rowCompleteWanted, rowForEvent,
} from '../txn/screen/detailsBuffer'
import type {
  ActionConfirmInfo, LookupResponse, RunActionResponse, ValidationErrorItem, WarningItem,
} from '../txn/contracts'
import type { ItemChangeFormChange, RowCompleteEvent, TransactionModel } from '../txn/types'
import {
  buildAssistantActionRequest, buildOpenActionRequest, buildPaymentActionRequest, buildSendActionRequest,
  normalizeItemChange, resolveReportActionParams,
} from '../txn/types'
import { buildParentPk, columnDbName, columnFormKey, literalDefaults } from '../txn/utils'
import { isInvokableRule } from '../txn/list/useItemChange'
import { attemptWithReason, attemptWithWarningConfirm } from '../txn/warnings'
import { attemptActionWithConfirm, type ConfirmHelpers } from '../txn/ActionConfirmModal'
import { lineEntryOf } from '../txn/lineEntry/model'
import { lineEntryApi, toBufferRow, type LineRowResult } from '../txn/lineEntry/contract'
import type {
  ValidateTreeIssue, ValidateTreeResponse, WizardConfirmResponse, WizardFetchResponse, WizardFileResponse,
  WizardFrame, WizardHost, WizardSendResponse,
  WizardPollCondition, WizardPollTicket, WizardRedirectStartResponse, WizardRedirectStatusResponse,
  WizardModel, WizardOp, WizardScanResponse, WizardSet, WizardStep, WizardVia,
} from './contract'
import {
  columnOf, evaluateExpr, frameValues, isBlank, keyOf, liveRows, lookupFilter, parseTarget, readCol,
  renderMap, renderTemplate, routeOf, scopeOf, stepById, stripControl, text,
  type FormIndex, type Route, type Row, type Target, type WizardScope,
} from './model'
import { boundary, trail } from './machine'

export type WizardIssue = ValidateTreeIssue

/** Rows a chooser offers (a select_grid action, an `ask` line source, a
 *  record that matched more than once). */
export interface ChooseRequest {
  title: string
  rows: Row[]
  columns?: string[]
  multi?: boolean
  labelOf?: (col: string) => string
}

export interface OpsEnv {
  rt: TxnScreenRuntime
  project: string
  model: WizardModel
  base: TransactionModel
  fi: FormIndex
  via: WizardVia
  /** The step the operations run for (its `form_no`, `fetch_url` request…). */
  stepId?: string
  /** Index of this frame in the run's stack (idempotency key). */
  frameIndex?: number
  user?: { id?: string; name?: string }
  notify: (kind: 'success' | 'error' | 'info' | 'warning', message: string) => void
  host?: Pick<WizardHost, 'openObject' | 'openAssistant' | 'printReport'>
  // Interactive seams the runner supplies (absent ⇒ the default answer).
  confirmWarnings?: (warnings: WarningItem[]) => Promise<boolean>
  confirmAction?: (info: ActionConfirmInfo, helpers: ConfirmHelpers) => Promise<RunActionResponse>
  promptReason?: (message: string) => Promise<{ reason: string; reasonCode: string } | null>
  choose?: (req: ChooseRequest) => Promise<Row[] | null>
  showHtml?: (title: string, html: string) => void
  /** Extra scope for templates (`{scan.X}`, `{call.outcome}`…). */
  extra?: Partial<WizardScope>
}

export interface OpResult {
  frame: WizardFrame
  /** A goto / finish the operation asked for (ends a `then` list). */
  route?: Route | null
  issues: WizardIssue[]
  /** `{result.KEY}` for the next operation. */
  result?: Row
}

// ── Helpers ──────────────────────────────────────────────────────────────

let rowSeq = 0
/** A new line's client identity (the buffer's `_localId`). */
export function stampRow(row: Row): Row {
  if (row._localId) return row
  rowSeq += 1
  return { ...row, _localId: `wz${Date.now().toString(36)}${rowSeq}` }
}

export function scopeFor(env: OpsEnv, frame: WizardFrame, extra?: Partial<WizardScope>): WizardScope {
  return scopeOf(frame, env.fi, {
    step: stepById(env.model, env.stepId),
    run: { run_id: env.via.run_id },
    user: env.user,
    extra: { ...(env.extra || {}), ...(extra || {}) },
  })
}

function headerSm(env: OpsEnv) {
  return env.fi.forms[env.fi.headerNo]?.sm || null
}

function issue(message: string, code?: string | null, extra: Partial<WizardIssue> = {}): WizardIssue {
  return { message, code: code ?? null, type: 'E', ...extra }
}

/** Whether a column's item_change is worth a round trip (the form's rule). */
export function itemChangeInvokable(env: OpsEnv, formNo: string, name: string): boolean {
  const col = columnOf(env.fi, formNo, name)
  if (!col) return false
  const ic = normalizeItemChange(col.item_change)
  if (!ic) return false
  if (ic.mode === 'code' && !ic.code.trim()) return false
  if (ic.mode === 'rule' && (!ic.fill || ic.fill.length === 0)
    && (!ic.protect || ic.protect.length === 0)) return false
  return true
}

/** Engine issues out of either error envelope (platform axios `{error:{…}}`,
 *  standalone runtime `{status, detail:{errors, code, summary}}`). */
export function issuesFromError(err: unknown): WizardIssue[] {
  const e = err as {
    response?: { status?: number; data?: { error?: { code?: string; message?: string; details?: Record<string, unknown> }; detail?: unknown } }
    status?: number; detail?: unknown; message?: string
  }
  const fromList = (list: unknown, code: string | null, scope: unknown): WizardIssue[] =>
    (Array.isArray(list) ? list as Array<ValidationErrorItem & { form_no?: string; row?: number }> : []).map((x) => {
      const sc = (x.scope || scope) as { form_no?: string | null; path?: Array<{ form_no: string; index: number }> } | null
      const leaf = sc?.path?.length ? sc.path[sc.path.length - 1] : null
      return {
        code: x.code ?? code,
        field: x.field || null,
        form_no: leaf?.form_no ?? sc?.form_no ?? x.form_no ?? null,
        row: leaf ? leaf.index : (typeof x.row === 'number' ? x.row : null),
        message: x.message || 'Refused',
        type: x.severity === 'W' ? 'W' : 'E',
      }
    })
  const env = e?.response?.data?.error
  if (env && typeof env === 'object') {
    const details = (env.details || {}) as { errors?: unknown; warnings?: unknown; scope?: unknown }
    const list = [...fromList(details.errors, env.code || null, details.scope),
      ...fromList(details.warnings, env.code || null, details.scope).map(w => ({ ...w, type: 'W' as const }))]
    return list.length ? list : [issue(env.message || 'Request failed', env.code || null)]
  }
  const detail = e?.response?.data?.detail ?? e?.detail
  if (typeof detail === 'string' && detail.trim()) return [issue(detail)]
  if (detail && typeof detail === 'object') {
    const d = detail as { code?: string; errors?: unknown; warnings?: unknown; summary?: string; message?: string; scope?: unknown }
    const list = [...fromList(d.errors, d.code || null, d.scope),
      ...fromList(d.warnings, d.code || null, d.scope).map(w => ({ ...w, type: 'W' as const }))]
    return list.length ? list : [issue(d.summary || d.message || 'Request failed', d.code || null)]
  }
  return [issue(e?.message || 'Request failed')]
}

const blocking = (issues: WizardIssue[]) => issues.some(i => i.type === 'E')

// ── Values ───────────────────────────────────────────────────────────────

interface ItemChangeResponse {
  ok?: boolean
  updates?: Record<string, unknown>
  protect?: Record<string, 0 | 1>
  forms?: Record<string, ItemChangeFormChange>
  error?: string
  error_code?: string
  code?: string
  warning?: string
  warnings?: Array<{ message: string; code?: string | null }>
  prompts?: Array<{ message: string; code?: string | null }>
}

function noteMessages(env: OpsEnv, res: ItemChangeResponse | RunActionResponse, issues: WizardIssue[]): void {
  const r = res as ItemChangeResponse
  if (r.warnings?.length) {
    for (const w of r.warnings) {
      issues.push({ message: w.message, code: w.code ?? null, type: 'W' })
      env.notify('warning', w.message)
    }
  } else if (r.warning) {
    issues.push({ message: r.warning, code: null, type: 'W' })
    env.notify('warning', r.warning)
  }
  r.prompts?.forEach(p => env.notify('info', p.message))
}

/** Append / replace child-form rows a result carries (`forms: {"2": {rows}}`). */
function applyFormsRows(env: OpsEnv, frame: WizardFrame, forms: Record<string, ItemChangeFormChange> | undefined,
  skip: string[]): WizardFrame {
  if (!forms) return frame
  let details = frame.record.details
  for (const [fno, change] of Object.entries(forms)) {
    if (skip.includes(fno) || !formChangeTouchesRows(change)) continue
    details = { ...details, [fno]: applyFormRowsChange(details[fno], change, stampRow) }
  }
  return details === frame.record.details ? frame : { ...frame, record: { ...frame.record, details }, dirty: true }
}

/** Fire a column's item_change and merge what it returns — exactly the
 *  transaction screen's contract: `updates` + the form's own `set`, `protect`,
 *  other forms' rows; a header patch from a line's change. */
export async function fireItemChange(env: OpsEnv, frame: WizardFrame, t: Target): Promise<OpResult> {
  const issues: WizardIssue[] = []
  const { fi } = env
  const key = keyOf(fi, t.formNo, t.name)
  const header = frame.record.header
  try {
    if (t.scope === 'header') {
      const res = await env.rt.execute(env.base.obj_name, 'item_change', {
        field_name: key, header, header_values: {}, edit_mode: frame.txn_id ? 'edit' : 'add',
      }) as ItemChangeResponse
      if (res?.error) issues.push(issue(res.error, res.error_code || res.code || null, { field: key, form_no: fi.headerNo }))
      noteMessages(env, res || {}, issues)
      const hc = res?.forms?.[fi.headerNo]
      const updates = { ...(res?.updates || {}), ...(hc?.set || {}) }
      const protect = { ...(res?.protect || {}), ...(hc?.protect || {}) }
      let next: WizardFrame = {
        ...frame,
        record: { ...frame.record, header: { ...frame.record.header, ...updates } },
        protect: Object.keys(protect).length ? { ...(frame.protect || {}), ...protect } : frame.protect,
      }
      next = applyFormsRows(env, next, res?.forms, [fi.headerNo])
      return { frame: next, issues, result: updates }
    }
    if (t.scope !== 'line') return { frame, issues }
    const rows = frame.record.details[t.formNo] || []
    const idx = frame.line_index
    if (idx == null || frame.line_form !== t.formNo || !rows[idx]) return { frame, issues }
    const row = rows[idx]
    const res = await env.rt.execute(env.base.obj_name, 'item_change', {
      field_name: key, form_no: t.formNo, header: rowForEvent(row), header_values: header,
    }) as ItemChangeResponse
    if (res?.error) issues.push(issue(res.error, res.error_code || res.code || null, { field: key, form_no: t.formNo, row: idx }))
    noteMessages(env, res || {}, issues)
    const own = res?.forms?.[t.formNo]
    const updates = { ...(res?.updates || {}), ...(own?.set || {}) }
    const protect = { ...((row._protect as Record<string, 0 | 1>) || {}), ...(res?.protect || {}), ...(own?.protect || {}) }
    const nextRows = rows.slice()
    nextRows[idx] = { ...row, ...updates, ...(Object.keys(protect).length ? { _protect: protect } : {}) }
    const hset = res?.forms?.[fi.headerNo]?.set
    let next: WizardFrame = {
      ...frame,
      record: {
        header: hset ? { ...header, ...hset } : header,
        details: { ...frame.record.details, [t.formNo]: nextRows },
      },
    }
    next = applyFormsRows(env, next, res?.forms, [fi.headerNo, t.formNo])
    return { frame: next, issues, result: updates }
  } catch (err) {
    return { frame, issues: issuesFromError(err).map(i => ({ ...i, field: i.field || key })) }
  }
}

/** Write one value into the frame (no server call). */
export function writeValue(env: Pick<OpsEnv, 'fi'>, frame: WizardFrame, t: Target, value: unknown): WizardFrame | null {
  const { fi } = env
  if (t.scope === 'var') return { ...frame, vars: { ...frame.vars, [t.name]: value } }
  const key = keyOf(fi, t.formNo, t.name)
  if (t.scope === 'header') {
    return { ...frame, dirty: true, record: { ...frame.record, header: { ...frame.record.header, [key]: value } } }
  }
  const rows = frame.record.details[t.formNo] || []
  const idx = frame.line_index
  if (idx == null || frame.line_form !== t.formNo || !rows[idx] || rows[idx]._deleted) return null
  const nextRows = rows.slice()
  const row = rows[idx]
  nextRows[idx] = { ...row, [key]: value, ...(row._txn_id ? { _edited: true } : {}) }
  return { ...frame, dirty: true, record: { ...frame.record, details: { ...frame.record.details, [t.formNo]: nextRows } } }
}

/** Set a target the way typing it into the form would: the value, then that
 *  column's item_change (which may overwrite it — it wins, as on the form). */
export async function setValue(
  env: OpsEnv, frame: WizardFrame, target: string | Target, value: unknown,
  opts: { itemChange?: boolean } = {},
): Promise<OpResult> {
  const t = typeof target === 'string'
    ? parseTarget(target, env.fi, stepById(env.model, env.stepId)?.form_no || frame.line_form || env.fi.lineForm)
    : target
  if (!t) return { frame, issues: [issue(`"${String(target)}" is not a wizard target.`, 'WIZARD_TARGET')] }
  const written = writeValue(env, frame, t, value)
  if (!written) {
    return { frame, issues: [issue(`There is no current line to set ${t.name} on.`, 'WIZARD_NO_LINE', { form_no: t.formNo, field: t.name })] }
  }
  if (t.scope === 'var' || opts.itemChange === false || !itemChangeInvokable(env, t.formNo, t.name)) {
    return { frame: written, issues: [] }
  }
  return fireItemChange(env, written, t)
}

// ── Lines ────────────────────────────────────────────────────────────────

async function rowEvent(env: OpsEnv, frame: WizardFrame, form: string, idx: number, which: 'row_add' | 'row_edit'): Promise<OpResult> {
  const sm = env.fi.forms[form]?.sm
  if (!sm || !isInvokableRule(normalizeItemChange(which === 'row_add' ? sm.row_add : sm.row_edit))) {
    return { frame, issues: [] }
  }
  const rows = frame.record.details[form] || []
  try {
    const res = await env.rt.execute(env.base.obj_name, which, {
      form_no: form, header: rowForEvent(rows[idx]), header_values: frame.record.header,
    }) as ItemChangeResponse
    const issues: WizardIssue[] = []
    if (res?.error) issues.push(issue(res.error, res.error_code || null, { form_no: form, row: idx }))
    const updates = { ...(res?.updates || {}), ...(res?.forms?.[form]?.set || {}) }
    if (!Object.keys(updates).length) return { frame, issues }
    const next = rows.slice()
    next[idx] = { ...next[idx], ...updates }
    return { frame: { ...frame, record: { ...frame.record, details: { ...frame.record.details, [form]: next } } }, issues }
  } catch (err) {
    return { frame, issues: issuesFromError(err) }
  }
}

/** A new line on `form`: the form's literal defaults, its row_add event, then
 *  each `set` in order (item_change per column), optionally completed. */
export async function addLine(
  env: OpsEnv, frame: WizardFrame, form: string, set: Record<string, unknown> = {},
  opts: { complete?: boolean; row?: Row } = {},
): Promise<OpResult> {
  const sm = env.fi.forms[form]?.sm
  const rows = frame.record.details[form] || []
  const row = stampRow({ ...(sm ? literalDefaults(sm) : {}), ...(opts.row || {}) })
  let cur: WizardFrame = {
    ...frame,
    dirty: true,
    record: { ...frame.record, details: { ...frame.record.details, [form]: [...rows, row] } },
    line_form: form,
    line_index: rows.length,
  }
  const issues: WizardIssue[] = []
  const ev = await rowEvent(env, cur, form, rows.length, 'row_add')
  cur = ev.frame
  issues.push(...ev.issues)
  for (const [col, value] of Object.entries(set)) {
    if (blocking(issues)) break
    const t = parseTarget(col.includes('.') ? col : `line${form}.${col}`, env.fi, form)
    if (!t) continue
    const r = await setValue(env, cur, t, value)
    cur = r.frame
    issues.push(...r.issues)
  }
  if (opts.complete && !blocking(issues)) {
    const r = await completeLine(env, cur)
    cur = r.frame
    issues.push(...r.issues)
  }
  return { frame: cur, issues, result: { line_index: cur.line_index } }
}

/** Fire `row_complete` on the current line (the form's own rule): the line's
 *  values, its rebuilt child rows, and the header's recomputed totals. */
export async function completeLine(env: OpsEnv, frame: WizardFrame): Promise<OpResult> {
  const form = frame.line_form || env.fi.lineForm
  const idx = frame.line_index
  const rows = frame.record.details[form] || []
  if (idx == null || !rows[idx]) return { frame, issues: [issue('There is no current line to complete.', 'WIZARD_NO_LINE')] }
  const sm = env.fi.forms[form]?.sm
  const ev = sm?.row_complete
  const ic = normalizeItemChange(ev as RowCompleteEvent | string | undefined)
  if (!sm || !isInvokableRule(ic)) return { frame, issues: [] }
  const settings: Partial<RowCompleteEvent> = (typeof ev === 'object' && ev) ? ev : {}
  const row = rows[idx]
  const old = (row._wz_completed as Row | undefined) || null
  if (!rowCompleteWanted(settings.when_changed, row, old)) return { frame, issues: [] }
  try {
    const res = await env.rt.execute(env.base.obj_name, 'row_complete', {
      form_no: form,
      header: rowForEvent(row),
      header_values: frame.record.header,
      old: old ? rowForEvent(old) : {},
      children: childrenForEvent(row.__children as Record<string, Row[]> | undefined),
      lines: rows.filter((r, i) => i !== idx && !r._deleted).map(rowForEvent),
      ...(frame.txn_id ? { txn_id: frame.txn_id } : {}),
    }) as ItemChangeResponse
    const issues: WizardIssue[] = []
    if (res?.error || res?.ok === false) {
      return { frame, issues: [issue(res?.error || 'The line could not be completed', res?.error_code || null, { form_no: form, row: idx })] }
    }
    noteMessages(env, res || {}, issues)
    const forms = res?.forms || {}
    const ownSet = { ...(res?.updates || {}), ...(forms[form]?.set || {}) }
    const kids = applyFormsToChildren(row.__children as Record<string, Row[]> | undefined, forms,
      env.fi.children[form] || [], stampRow)
    const done: Row = { ...row, ...ownSet }
    if (kids.changed) done.__children = kids.children
    done._wz_completed = rowForEvent(done)
    const nextRows = rows.slice()
    nextRows[idx] = done
    const hset = forms[env.fi.headerNo]?.set
    return {
      frame: {
        ...frame,
        dirty: true,
        record: {
          header: hset ? { ...frame.record.header, ...hset } : frame.record.header,
          details: { ...frame.record.details, [form]: nextRows },
        },
      },
      issues,
      result: ownSet,
    }
  } catch (err) {
    return { frame, issues: issuesFromError(err).map(i => ({ ...i, form_no: i.form_no || form, row: i.row ?? idx })) }
  }
}

export function deleteLine(frame: WizardFrame): WizardFrame {
  const form = frame.line_form || ''
  const idx = frame.line_index
  const rows = frame.record.details[form] || []
  if (idx == null || !rows[idx]) return frame
  const next = rows[idx]._txn_id
    ? rows.map((r, i) => (i === idx ? { ...r, _deleted: true } : r))
    : rows.filter((_, i) => i !== idx)
  return { ...frame, dirty: true, line_index: null, record: { ...frame.record, details: { ...frame.record.details, [form]: next } } }
}

const norm = (v: unknown) => String(v ?? '').trim().toLowerCase()

/** Staged `line.*` values of a scan (`map` before a match_line / add_line):
 *  what the scanned label says, before we know which line it is about. */
export type PendingLine = Record<string, unknown>

/** `match_line` (§6.4): the line whose `on` columns equal the scanned values.
 *  None → `on_none` (add_line / goto / error); several → `on_many`. A line
 *  form with a quick-add source builds a missing line through the engine's
 *  `match_lines` (its own source and rules) instead of an empty one. */
export async function matchLine(env: OpsEnv, frame: WizardFrame, op: WizardOp, pending: PendingLine): Promise<OpResult> {
  const form = String(op.form_no || stepById(env.model, env.stepId)?.form_no || frame.line_form || env.fi.lineForm)
  const scope = scopeFor(env, frame)
  const on = (op.on || []).map(String)
  const rows = frame.record.details[form] || []
  const wanted = (col: string): unknown => {
    for (const [k, v] of Object.entries(pending)) {
      const t = parseTarget(k, env.fi, form)
      if (t && t.scope !== 'var' && keyOf(env.fi, form, t.name) === keyOf(env.fi, form, col)) return v
    }
    return scope.scan?.[col] ?? scope.scan?.[col.toLowerCase()]
  }
  const hits = rows.map((_, i) => i).filter((i) => {
    if (rows[i]._deleted) return false
    return on.length > 0 && on.every(c => norm(readCol(rows[i], env.fi, form, c)) === norm(wanted(c)))
  })
  const routeFor = (spec: string | undefined, dflt: string): OpResult | null => {
    const s = String(spec || dflt)
    // The message is a template ("Item {scan.item} is not on order {header.sord_no}").
    if (s.startsWith('error:')) return { frame, issues: [issue(text(s.slice(6).trim(), scope) || 'No matching line', 'WIZARD_NO_MATCH')] }
    if (s === 'add_line') return null
    const r = routeOf(s)
    return r ? { frame, issues: [], route: r } : { frame, issues: [issue('No matching line', 'WIZARD_NO_MATCH')] }
  }
  let cur = frame
  const issues: WizardIssue[] = []
  if (hits.length === 0) {
    const routed = routeFor(op.on_none, 'error:Not on this document')
    if (routed) return routed
    // add_line: through the line form's quick-add source when it has one.
    const le = lineEntryOf(env.fi.forms[form]?.sm)
    const code = on.length ? wanted(on[0]) : undefined
    if (le?.quickAdd && !isBlank(code)) {
      try {
        const res = await env.rt.execute(env.base.obj_name, 'match_lines', {
          form_no: form, header_values: frame.record.header, code, qty: 1,
          details: { [form]: liveRows(rows).map(rowForEvent) },
          ...(frame.txn_id ? { txn_id: frame.txn_id } : {}),
        }) as { rows?: LineRowResult[] }
        const hit = res?.rows?.[0]
        if (hit) {
          const r = await addLine(env, cur, form, {}, { row: toBufferRow(hit) })
          cur = r.frame
          issues.push(...r.issues)
        }
      } catch (err) {
        return { frame, issues: issuesFromError(err) }
      }
    }
    if (cur === frame) {
      const r = await addLine(env, cur, form)
      cur = r.frame
      issues.push(...r.issues)
    }
  } else if (hits.length > 1 && op.on_many && op.on_many !== 'first') {
    const routed = routeFor(op.on_many, 'error:More than one line matches')
    if (routed) return routed
  } else {
    cur = { ...cur, line_form: form, line_index: hits[0] }
  }
  // The scanned values land on the line (they matched on `on` already).
  for (const [k, v] of Object.entries(pending)) {
    if (blocking(issues)) break
    const t = parseTarget(k, env.fi, form)
    if (!t) continue
    if (t.scope !== 'var' && hits.length && on.some(c => keyOf(env.fi, form, c) === keyOf(env.fi, form, t.name))) continue
    const r = await setValue(env, cur, t, v)
    cur = r.frame
    issues.push(...r.issues)
  }
  if (op.increment?.column && !blocking(issues)) {
    const t = parseTarget(op.increment.column, env.fi, form)
    if (t && t.scope === 'line') {
      const by = Number(renderTemplate(op.increment.by ?? 1, scopeFor(env, cur)).value ?? 1)
      const was = Number(readCol(cur.record.details[form]?.[cur.line_index ?? -1], env.fi, form, t.name) || 0)
      const r = await setValue(env, cur, t, (Number.isFinite(was) ? was : 0) + (Number.isFinite(by) ? by : 1))
      cur = r.frame
      issues.push(...r.issues)
    }
  }
  return { frame: cur, issues, result: { line_index: cur.line_index, matched: hits.length } }
}

// ── Record ───────────────────────────────────────────────────────────────

/** A new record: the form's literal defaults, then the server's resolution
 *  (project variables, Vision itm_default) — the Add form's two steps. */
export async function initAdd(env: OpsEnv, frame: WizardFrame): Promise<OpResult> {
  const header: Row = literalDefaults(headerSm(env))
  let protect: Record<string, 0 | 1> = {}
  try {
    const res = await env.rt.resolveDefaults(env.base.obj_name)
    Object.assign(header, res?.values || {})
    protect = res?.protect || {}
  } catch (err) {
    // Defaults are a convenience: the save still validates.
    if (typeof console !== 'undefined') console.debug('[wizard] resolve-defaults failed', err)
  }
  return {
    frame: {
      ...frame, mode: frame.mode === 'edit' ? 'add' : frame.mode, txn_id: null,
      record: { header, details: {} }, protect, dirty: false, save_status: 'none', line_index: null,
    },
    issues: [],
  }
}

/** Rows loaded at once while walking a record's tree (one list per row per
 *  child form — bounded so a long document does not open a hundred
 *  requests together). */
const TREE_FANOUT = 6

/** Every saved row of `form` under `parentPk`, each carrying its OWN child
 *  forms' rows under `__children` — the transaction screen's buffer shape,
 *  which the batch save takes back unchanged: an untouched saved row is not
 *  rewritten, and its children are saved under its key. Loading the whole
 *  tree is what lets a line's `row_complete` replace its children: rows it
 *  never saw would otherwise survive beside the rebuilt ones. */
export async function loadFormTree(env: Pick<OpsEnv, 'rt' | 'base' | 'fi'>, form: string,
  parentPk: Record<string, unknown>): Promise<Row[]> {
  const rows = await listAllChildRows((q) => env.rt.list(env.base.obj_name, q), form, parentPk) as Row[]
  const kids = env.fi.children[form] || []
  const sm = env.fi.forms[form]?.sm
  if (!kids.length || !sm) return rows
  const out: Row[] = rows.slice()
  for (let i = 0; i < rows.length; i += TREE_FANOUT) {
    await Promise.all(rows.slice(i, i + TREE_FANOUT).map(async (row, k) => {
      const pk = buildParentPk(sm, row)
      if (!Object.keys(pk).length) return
      const children: Record<string, Row[]> = {}
      for (const gc of kids) children[gc] = await loadFormTree(env, gc, pk)
      out[i + k] = { ...row, __children: children }
    }))
  }
  return out
}

/** Load a saved record: the header and its whole tree of lines (every child
 *  form, and their own child forms under each row's `__children`). */
export async function loadRecord(env: OpsEnv, frame: WizardFrame, txnId: string): Promise<OpResult> {
  try {
    const res = await env.rt.load(env.base.obj_name, txnId)
    const header: Row = { ...(res.header || {}) }
    delete header._txn_id
    Object.assign(header, res.item_defaults?.updates || {})
    const sm = headerSm(env)
    const details: Record<string, Row[]> = {}
    const pk = sm ? buildParentPk(sm, header) : {}
    for (const fno of env.fi.children[env.fi.headerNo] || []) {
      details[fno] = Object.keys(pk).length ? await loadFormTree(env, fno, pk) : []
    }
    if (res.editable === false) {
      return { frame, issues: [issue(res.lock_reason || 'This record cannot be changed.', 'RECORD_LOCKED')] }
    }
    return {
      frame: {
        ...frame, mode: 'edit', txn_id: txnId, record: { header, details },
        protect: res.item_defaults?.protect || {}, dirty: false, save_status: 'none', line_index: null,
      },
      issues: [],
    }
  } catch (err) {
    return { frame, issues: issuesFromError(err) }
  }
}

/** Saved records matching `match` (template values; a token that does not
 *  resolve refuses the lookup — the `open` action's rule). */
export async function findRecords(env: OpsEnv, frame: WizardFrame, match: Record<string, unknown>,
  limit = 20): Promise<{ rows: Row[]; issues: WizardIssue[] }> {
  const { values, missing } = renderMap(match, scopeFor(env, frame))
  if (missing.length) {
    return { rows: [], issues: [issue(`Cannot look the record up — no value for ${missing.join(', ')}.`, 'WIZARD_MATCH_MISSING')] }
  }
  const filters = Object.entries(values).map(([col, value]) => {
    const c = columnOf(env.fi, env.fi.headerNo, col)
    return { column: c ? columnDbName(c) : col.toLowerCase(), op: 'eq' as const, value }
  })
  try {
    const res = await env.rt.list(env.base.obj_name, { limit, offset: 0, explicitLimit: true, filters, bypass: true })
    return { rows: (res?.rows || []) as Row[], issues: [] }
  } catch (err) {
    return { rows: [], issues: issuesFromError(err) }
  }
}

/** `locate` / a `load` step: one match → edit it; none → `onMissing`; several
 *  → the user picks. */
export async function locate(
  env: OpsEnv, frame: WizardFrame, match: Record<string, unknown>,
): Promise<{ frame: WizardFrame; found: 'one' | 'none' | 'cancelled'; issues: WizardIssue[] }> {
  const { rows, issues } = await findRecords(env, frame, match)
  if (issues.length) return { frame, found: 'none', issues }
  if (!rows.length) return { frame, found: 'none', issues: [] }
  let pick = rows[0]
  if (rows.length > 1) {
    if (!env.choose) return { frame, found: 'none', issues: [issue('More than one record matches.', 'WIZARD_MANY')] }
    const chosen = await env.choose({ title: 'Several records match — pick one', rows, multi: false })
    if (!chosen?.length) return { frame, found: 'cancelled', issues: [] }
    pick = chosen[0]
  }
  const r = await loadRecord(env, frame, String(pick._txn_id))
  return { frame: r.frame, found: r.issues.length ? 'none' : 'one', issues: r.issues }
}

/** The frame's record, as the batch save / validate_tree take it. */
export function recordPayload(frame: WizardFrame): { header: Row; details: Record<string, Row[]> } {
  const clean = (row: Row): Row => {
    const out: Row = {}
    for (const [k, v] of Object.entries(row)) if (!k.startsWith('_wz_')) out[k] = v
    if (out.__children) out.__children = cleanTree(out.__children as Record<string, Row[]>)
    return out
  }
  const cleanTree = (tree: Record<string, Row[]>) => {
    const out: Record<string, Row[]> = {}
    for (const [fno, rows] of Object.entries(tree || {})) out[fno] = (rows || []).map(clean)
    return out
  }
  return { header: frame.record.header, details: cleanTree(frame.record.details) }
}

/** `validate_tree`: the whole unsaved record dry-run (§11.2). */
export async function validateTree(env: OpsEnv, frame: WizardFrame): Promise<WizardIssue[]> {
  try {
    const res = await env.rt.execute(env.base.obj_name, 'validate_tree', {
      ...recordPayload(frame), ...(frame.txn_id ? { txn_id: frame.txn_id } : {}),
    }) as ValidateTreeResponse
    return [...(res?.errors || []).map(e => ({ ...e, type: e.type || 'E' as const })),
      ...(res?.warnings || []).map(w => ({ ...w, type: 'W' as const }))]
  } catch (err) {
    return issuesFromError(err)
  }
}

export interface SaveOutcome {
  frame: WizardFrame
  status: 'saved' | 'pending_approval' | 'failed' | 'cancelled'
  issues: WizardIssue[]
  message?: string
}

function approvalEnvelopeOf(res: unknown): { message: string; ref?: string } | null {
  if (!res || typeof res !== 'object') return null
  const r = res as { approval?: { instance_id?: unknown; id?: unknown }; message?: unknown }
  if (!r.approval || typeof r.approval !== 'object') return null
  const ref = r.approval.instance_id ?? r.approval.id
  return { message: typeof r.message === 'string' ? r.message : '', ref: ref == null ? undefined : String(ref) }
}

/** The `save` step (§6.12): execute-batch with the frame's record, warnings
 *  through the shared confirm, reason-for-change through the shared prompt,
 *  an idempotency key from (run, frame, save number). */
export async function saveRecord(env: OpsEnv, frame: WizardFrame, step: WizardStep | null): Promise<SaveOutcome> {
  const seq = (frame.trail || []).filter(t => t.kind === 'save').length + 1
  const key = `${env.via.run_id}:${env.frameIndex ?? 0}:${seq}`
  const scope = scopeFor(env, frame)
  const reasonSpec = step?.reason
  const fixedReason = reasonSpec && reasonSpec !== 'ask' && reasonSpec !== 'none' ? text(reasonSpec, scope) : ''
  const payload = recordPayload(frame)
  const confirm = step?.warnings === 'fail' ? (async () => false) : (env.confirmWarnings || (async () => false))
  const prompt = reasonSpec === 'none' ? (async () => null) : (env.promptReason || (async () => null))
  let res: BatchSaveResult
  try {
    res = await attemptWithReason(
      (reason, reasonCode) => attemptWithWarningConfirm(
        (ignore) => env.rt.saveBatch(env.base.obj_name, {
          header: payload.header,
          details: payload.details,
          txn_id: frame.txn_id || undefined,
          apply_item_change: true,
          idempotency_key: key,
          ...((reason || fixedReason) ? { reason: reason || fixedReason, reason_code: reasonCode } : {}),
          ...(ignore.length ? { ignore_warnings: ignore } : {}),
        } as BatchSaveBody),
        confirm,
      ),
      prompt,
    )
  } catch (err) {
    return { frame: trail(frame, 'issue', step?.id, 'save refused'), status: 'failed', issues: issuesFromError(err) }
  }
  const staged = approvalEnvelopeOf(res)
  if (staged) {
    const next = boundary(trail({ ...frame, save_status: 'pending_approval', dirty: false }, 'save', step?.id,
      `pending approval${staged.ref ? ` ${staged.ref}` : ''}`))
    return {
      frame: { ...next, vars: { ...next.vars, ...(staged.ref ? { _approval_ref: staged.ref } : {}) } },
      status: 'pending_approval', issues: [],
      message: staged.message || 'Submitted for approval.',
    }
  }
  const header: Row = { ...(res.header || frame.record.header) }
  delete header._txn_id
  res.prompts?.forEach(p => env.notify('info', p.message))
  const saved: WizardFrame = {
    ...frame,
    txn_id: res.txn_id || frame.txn_id || null,
    mode: 'edit',
    save_status: 'saved',
    dirty: false,
    record: { header, details: res.details ? markChildrenSaved(res.details) as Record<string, Row[]> : frame.record.details },
  }
  return { frame: boundary(trail(saved, 'save', step?.id, String(saved.txn_id || ''))), status: 'saved', issues: [] }
}

// ── run_action ───────────────────────────────────────────────────────────

/** Run a base-transaction action button with the unsaved record and apply
 *  what it returns, by handler (§7.4). */
export async function runAction(env: OpsEnv, frame: WizardFrame, name: string): Promise<OpResult> {
  const { fi } = env
  const payload = recordPayload(frame)
  let res: RunActionResponse
  try {
    res = await attemptActionWithConfirm(
      (confirm) => attemptWithWarningConfirm(
        (ignore) => env.rt.execute(env.base.obj_name, 'run_action', {
          form_no: fi.headerNo,
          action_name: name,
          header: payload.header,
          header_values: {},
          details: payload.details,
          ...(frame.txn_id ? { txn_id: frame.txn_id } : {}),
          ...(ignore.length ? { ignore_warnings: ignore } : {}),
          ...(confirm ? { confirm } : {}),
        }) as Promise<RunActionResponse>,
        env.confirmWarnings || (async () => false),
      ),
      env.confirmAction || (async () => { throw Object.assign(new Error('confirmation needed'), { __actionConfirmCancelled: true }) }),
    )
  } catch (err) {
    if ((err as Record<string, unknown>)?.__actionConfirmCancelled) {
      return { frame, issues: [issue('The action was not confirmed.', 'ACTION_CANCELLED', { type: 'W' })] }
    }
    return { frame, issues: issuesFromError(err) }
  }
  const issues: WizardIssue[] = []
  noteMessages(env, res, issues)
  const header = frame.record.header
  const host = env.host || {}
  let cur = frame
  switch (res.handler) {
    case 'system':
      return { frame, issues: [issue('Saving / adding / deleting is the wizard\'s own save step — this action cannot run here.', 'WIZARD_SYSTEM_ACTION')] }
    case 'message':
      if (res.message) env.notify('success', res.message)
      break
    case 'html_modal':
      env.showHtml?.(name, res.html || '')
      break
    case 'report': {
      const rep = res.report
      if (rep?.obj_name && host.printReport) {
        await host.printReport({
          objName: rep.obj_name, params: resolveReportActionParams(rep.param_map, header),
          outputFormat: rep.output_format, delivery: 'preview', title: name,
        })
      } else env.notify('error', 'Report viewing is not available here.')
      break
    }
    case 'open': {
      const req = buildOpenActionRequest(name, res.open, env.base.obj_name, header, header)
      if (typeof req === 'string') env.notify('error', req)
      else if (host.openObject) host.openObject({ ...req, presentation: req.presentation || 'drawer' })
      else env.notify('error', 'Opening another screen is not available here.')
      break
    }
    case 'assistant': {
      const req = buildAssistantActionRequest(name, res.assistant, header, header)
      if (typeof req === 'string') env.notify('error', req)
      else if (host.openAssistant) host.openAssistant(req)
      else env.notify('error', 'The Assistant is not available here.')
      break
    }
    case 'payment': {
      const open = env.rt.txn.openPaymentAction
      const req = buildPaymentActionRequest(name, res.payment, env.base.obj_name, frame.txn_id || undefined)
      if (typeof req === 'string') env.notify('error', req)
      else if (open) open(req)
      else env.notify('error', 'Payments are not available here.')
      break
    }
    case 'select_grid': {
      let rows = (res.rows || []) as Row[]
      if (rows.length && env.choose) rows = (await env.choose({ title: name, rows, columns: res.columns, multi: true })) || []
      const form = String(res.target_form_no || fi.lineForm)
      if (rows.length) {
        const existing = cur.record.details[form] || []
        cur = {
          ...cur, dirty: true,
          record: { ...cur.record, details: { ...cur.record.details, [form]: [...existing, ...rows.map(r => stampRow({ ...r }))] } },
        }
      }
      break
    }
    case 'import':
      env.notify('error', 'Importing a file is not available inside a wizard.')
      break
    case 'send': {
      // The send window over the wizard's saved record (a record not saved
      // yet has nothing to send — the window says so).
      const open = env.rt.txn.openSendAction
      if (open) {
        open(buildSendActionRequest(name, res.send, env.base.obj_name,
          frame.txn_id ? [String(frame.txn_id)] : []))
      } else env.notify('error', 'Sending messages is not available here.')
      break
    }
    case 'item_change':
    default: {
      const hset = { ...(res.updates || {}), ...(res.forms?.[fi.headerNo]?.set || {}) }
      if (Object.keys(hset).length) cur = { ...cur, dirty: true, record: { ...cur.record, header: { ...cur.record.header, ...hset } } }
      cur = applyFormsRows(env, cur, res.forms as Record<string, ItemChangeFormChange> | undefined, [fi.headerNo])
      if (res.message) env.notify('success', res.message)
      break
    }
  }
  return { frame: cur, issues, result: { handler: res.handler, message: res.message ?? '' } }
}

// ── Server-side wizard operations ────────────────────────────────────────

/** Apply `[{target, value}]` sets in order (item_change each). */
export async function applySets(env: OpsEnv, frame: WizardFrame, sets: WizardSet[]): Promise<OpResult> {
  let cur = frame
  const issues: WizardIssue[] = []
  for (const s of sets || []) {
    if (blocking(issues)) break
    const r = await setValue(env, cur, s.target, s.value)
    cur = r.frame
    issues.push(...r.issues)
  }
  return { frame: cur, issues }
}

/** `wizard_scan`: the server parses the text with the STEP's rules (never the
 *  browser's) and returns the fields and the sets they map to. */
export async function scanParse(env: OpsEnv, frame: WizardFrame, stepId: string, textIn: string): Promise<WizardScanResponse> {
  return await env.rt.execute(env.model.obj_name, 'wizard_scan', {
    step_id: stepId, text: textIn, frame: frameValues(scopeFor(env, frame)),
  }) as WizardScanResponse
}

/** `wizard_confirm`: a confirm step's `confirm.password` — the server checks
 *  the caller's OWN password through the host's confirmation provider. The
 *  password goes to the server once and is never kept. */
export async function confirmPassword(env: Pick<OpsEnv, 'rt' | 'model'>, stepId: string,
  password: string): Promise<WizardConfirmResponse> {
  try {
    return await env.rt.execute(env.model.obj_name, 'wizard_confirm', {
      step_id: stepId, password,
    }) as WizardConfirmResponse
  } catch (err) {
    const first = issuesFromError(err)[0]
    return { ok: false, verified: false, code: first?.code || undefined, message: first?.message || 'Could not check the password.' }
  }
}

/** `wizard_file` (a print step with a `file` block) / `wizard_send` (a send
 *  step): the body names only the step and the saved record; the report,
 *  repository, send button and template are the MODEL's, and the server
 *  re-reads the record. A refusal comes back as `{ok: false, error, code}`. */
export async function documentOp(env: Pick<OpsEnv, 'rt' | 'model'>,
  op: 'wizard_file' | 'wizard_send', stepId: string, txnId: string | null | undefined,
  frame: unknown): Promise<WizardFileResponse & WizardSendResponse> {
  if (!txnId) return { ok: false, code: 'WIZARD_NOT_SAVED', error: 'Save the record first.' }
  try {
    return await env.rt.execute(env.model.obj_name, op, {
      step_id: stepId, txn_id: txnId, frame,
    }) as WizardFileResponse & WizardSendResponse
  } catch (err) {
    const first = issuesFromError(err)[0]
    return { ok: false, code: first?.code || undefined,
             error: first?.message || (op === 'wizard_file' ? 'Could not file the report.' : 'Could not send.') }
  }
}

/** `wizard_fetch`: the server reads the URL from the MODEL and maps it. */
export async function fetchUrl(env: OpsEnv, frame: WizardFrame, stepId: string): Promise<OpResult & { failed?: boolean }> {
  return serverAnswer(env, frame, 'wizard_fetch', { step_id: stepId }, 'WIZARD_FETCH_FAILED')
}

/** `wizard_function` (a `function` step): the server resolves the step's
 *  credential, runs its sandboxed function and maps the answer — applied
 *  exactly like a fetch_url answer. */
export async function callFunction(env: OpsEnv, frame: WizardFrame, stepId: string): Promise<OpResult & { failed?: boolean; poll?: WizardPollTicket }> {
  let res: WizardFetchResponse
  try {
    res = await env.rt.execute(env.model.obj_name, 'wizard_function', {
      step_id: stepId, run_id: env.via.run_id, frame: frameValues(scopeFor(env, frame)),
    }) as WizardFetchResponse
  } catch (err) {
    return { frame, issues: issuesFromError(err), failed: true }
  }
  // A started job the browser watches: nothing to apply yet.
  if (res?.ok && res.pending && res.poll?.url) {
    return { frame, issues: [], poll: res.poll, result: { message: res.message ?? null } }
  }
  return applyAnswer(env, frame, res, 'WIZARD_FUNCTION_FAILED')
}

/** A watched job is done (the browser saw it) — `wizard_poll_finish` runs the
 *  step's finish on the server; or, when the browser cannot read the status
 *  address, `wizard_poll_check` lets the server look (`server: true`). */
export async function pollStep(env: OpsEnv, frame: WizardFrame, stepId: string, server: boolean): Promise<OpResult & { failed?: boolean; state: string }> {
  let res: WizardFetchResponse
  try {
    res = await env.rt.execute(env.model.obj_name, server ? 'wizard_poll_check' : 'wizard_poll_finish', {
      step_id: stepId, run_id: env.via.run_id, frame: frameValues(scopeFor(env, frame)),
    }) as WizardFetchResponse
  } catch (err) {
    return { frame, issues: issuesFromError(err), failed: true, state: 'error' }
  }
  if (res?.state === 'waiting') return { frame, issues: [], state: 'waiting' }
  const out = await applyAnswer(env, frame, res, 'WIZARD_POLL_FAILED')
  return { ...out, state: res?.state || (res?.ok ? 'done' : 'failed') }
}

/** Minimal JSONPath (`$`, `.key`, `[n]`, `['key']`) — the server's subset. */
export function jsonPathValue(data: unknown, path: string): unknown {
  const p = String(path || '').trim()
  if (!p.startsWith('$')) return undefined
  const re = /\.([A-Za-z_$][\w$-]*)|\[\s*(\d+)\s*\]|\[\s*'([^']*)'\s*\]|\[\s*"([^"]*)"\s*\]/y
  let node: unknown = data
  let i = 1
  while (i < p.length) {
    re.lastIndex = i
    const m = re.exec(p)
    if (!m) return undefined
    i = re.lastIndex
    const key = m[1] ?? m[3] ?? m[4]
    if (key !== undefined) node = node && typeof node === 'object' && !Array.isArray(node) ? (node as Record<string, unknown>)[key] : undefined
    else node = Array.isArray(node) ? node[Number(m[2])] : undefined
    if (node === undefined) return undefined
  }
  return node
}

/** A `done` / `fail` condition — the same rule as the server's `poll.matches`. */
export function pollMatches(data: unknown, cond: WizardPollCondition | undefined | null): boolean {
  if (!cond || !String(cond.path || '').trim()) return false
  const v = jsonPathValue(data, cond.path)
  if (cond.exists === true) return v !== undefined && v !== null
  if (v !== null && typeof v === 'object') return false
  const norm = (x: unknown) => String(x ?? '').trim().toLowerCase()
  if ('equals' in cond) return norm(v) === norm(cond.equals)
  if (Array.isArray(cond.in)) return cond.in.map(norm).includes(norm(v))
  return false
}

/** `wizard_redirect_start`: mint the callback and get the URL to open. */
export async function redirectStart(env: OpsEnv, frame: WizardFrame, stepId: string): Promise<WizardRedirectStartResponse> {
  try {
    return await env.rt.execute(env.model.obj_name, 'wizard_redirect_start', {
      step_id: stepId, run_id: env.via.run_id, frame: frameValues(scopeFor(env, frame)),
    }) as WizardRedirectStartResponse
  } catch (err) {
    const first = issuesFromError(err)[0]
    return { ok: false, error: first?.message || 'Could not open the other site.', code: first?.code || undefined }
  }
}

/** `wizard_redirect_status`: waiting / expired, or — once the other site
 *  called back — its mapped answer, applied like a fetch_url answer. */
export async function redirectStatus(env: OpsEnv, frame: WizardFrame, stepId: string): Promise<OpResult & { failed?: boolean; state: WizardRedirectStatusResponse['state'] }> {
  let res: WizardRedirectStatusResponse
  try {
    res = await env.rt.execute(env.model.obj_name, 'wizard_redirect_status', {
      step_id: stepId, run_id: env.via.run_id, frame: frameValues(scopeFor(env, frame)),
    }) as WizardRedirectStatusResponse
  } catch (err) {
    return { frame, issues: issuesFromError(err), failed: true, state: 'waiting' }
  }
  if (res?.state !== 'received' && !(res?.state === 'expired')) return { frame, issues: [], state: res?.state || 'waiting' }
  const out = await applyAnswer(env, frame, res, 'WIZARD_REDIRECT_FAILED')
  return { ...out, state: res.state }
}

async function serverAnswer(env: OpsEnv, frame: WizardFrame, action: string, body: Record<string, unknown>,
  failCode: string): Promise<OpResult & { failed?: boolean }> {
  let res: WizardFetchResponse
  try {
    res = await env.rt.execute(env.model.obj_name, action, {
      ...body, frame: frameValues(scopeFor(env, frame)),
    }) as WizardFetchResponse
  } catch (err) {
    return { frame, issues: issuesFromError(err), failed: true }
  }
  return applyAnswer(env, frame, res, failCode)
}

/** A server answer (`WizardFetchResponse`) into the frame: each set through
 *  the typing path (item_change), each line through `add_line`. A refused
 *  answer is an issue carrying its own code (so `on_issue` can route it). */
export async function applyAnswer(env: OpsEnv, frame: WizardFrame, res: WizardFetchResponse | null | undefined,
  failCode: string): Promise<OpResult & { failed?: boolean }> {
  if (!res?.ok) return { frame, issues: [issue(res?.error || 'The request failed', res?.code || failCode)], failed: true }
  const r = await applySets(env, frame, res.sets || [])
  let cur = r.frame
  if (res.lines?.rows?.length) {
    const form = String(res.lines.form_no || env.fi.lineForm)
    for (const row of res.lines.rows) {
      const added = await addLine(env, cur, form, row)
      cur = added.frame
      r.issues.push(...added.issues)
    }
  }
  for (const w of res.warnings || []) r.issues.push({ message: w.message, code: w.code || null, type: 'W' })
  return { frame: cur, issues: r.issues, result: { status: res.status ?? null, message: res.message ?? null } }
}

// ── The operation list ───────────────────────────────────────────────────

/** Run one operation. */
export async function runOp(env: OpsEnv, frame: WizardFrame, op: WizardOp, ctx: { pending?: PendingLine; result?: Row } = {}): Promise<OpResult> {
  const scope = scopeFor(env, frame, ctx.result ? { result: ctx.result } : undefined)
  const step = stepById(env.model, env.stepId)
  const form = String(op.form_no || step?.form_no || frame.line_form || env.fi.lineForm)
  switch (op.op) {
    case 'set': {
      if (!op.target) return { frame, issues: [issue('A set operation names no target.', 'WIZARD_TARGET')] }
      let value: unknown
      if (op.expr) {
        try { value = evaluateExpr(op.expr, scope) } catch (err) {
          return { frame, issues: [issue(`Could not compute ${op.target}: ${(err as Error).message}`, 'WIZARD_EXPR')] }
        }
      } else {
        const r = renderTemplate(op.value, scope)
        // An unresolved token skips the set (the open action's rule).
        if (r.missing.length) return { frame, issues: [] }
        value = r.value
      }
      return setValue(env, frame, op.target, value, { itemChange: op.item_change !== false })
    }
    case 'item_change': {
      const t = op.field ? parseTarget(op.field, env.fi, form) : null
      if (!t || t.scope === 'var') return { frame, issues: [] }
      if (!itemChangeInvokable(env, t.formNo, t.name)) return { frame, issues: [] }
      return fireItemChange(env, frame, t)
    }
    case 'add_line': {
      const { values } = renderMap(op.set || {}, scope)
      return addLine(env, frame, form, values, { complete: !!op.complete })
    }
    case 'complete_line':
      return completeLine(env, frame)
    case 'delete_line':
      return { frame: deleteLine(frame), issues: [] }
    case 'match_line':
      return matchLine(env, frame, op, ctx.pending || {})
    case 'lookup': {
      if (!op.lookup) return { frame, issues: [issue('A lookup operation names no lookup.', 'WIZARD_LOOKUP')] }
      const value = renderTemplate(op.value, scope).value
      let res: LookupResponse
      try {
        res = await env.rt.txn.lookup(env.project, env.base.obj_name, op.lookup, isBlank(value) ? '' : String(value), 20, {
          ...lookupFilter(op.filter, scope), formValues: scope.line || scope.header, headerValues: scope.header,
        })
      } catch (err) {
        return { frame, issues: issuesFromError(err) }
      }
      const rows = (res?.rows || []) as unknown as Array<Row & { data?: unknown }>
      const hit = rows.find(r => norm(r.data) === norm(value)) || (rows.length === 1 ? rows[0] : null)
      if (!hit) return { frame, issues: [issue(`"${String(value ?? '')}" was not found.`, 'WIZARD_LOOKUP_NONE')] }
      let cur = frame
      const issues: WizardIssue[] = []
      for (const [target, col] of Object.entries(op.map || {})) {
        const v = readCol(hit, env.fi, '', col) ?? (col.toLowerCase() === 'data' ? hit.data : undefined)
        const r = await setValue(env, cur, target, v)
        cur = r.frame
        issues.push(...r.issues)
      }
      return { frame: cur, issues, result: stripControl(hit) }
    }
    case 'fetch_lines': {
      if (!op.source) return { frame, issues: [issue('fetch_lines names no source.', 'WIZARD_LINES')] }
      const exec = (a: string, p: Record<string, unknown>) => env.rt.execute(env.base.obj_name, a, p)
      const sm = env.fi.forms[env.fi.headerNo]?.sm
      const api = lineEntryApi(exec, {
        formNo: form, headerValues: frame.record.header, txnId: frame.txn_id || undefined,
        parentPk: sm ? buildParentPk(sm, frame.record.header) : undefined,
        lines: frame.record.details[form] || [],
      })
      try {
        let picks: Array<{ _src_key: string; values?: Row }> | 'all' = 'all'
        if (op.select === 'ask') {
          const fetched = await api.fetch(op.source)
          const offered = (fetched.rows || []).map(r => ({ ...r.values, _src_key: r._src_key }))
          const chosen = env.choose ? await env.choose({ title: fetched.command_label || 'Pick lines', rows: offered, multi: true }) : offered
          if (!chosen?.length) return { frame, issues: [] }
          picks = chosen.map(r => ({ _src_key: String(r._src_key) }))
        }
        const applied = await api.apply(op.source, picks)
        const rows = (applied.rows || []).map(r => stampRow(toBufferRow(r)))
        if (!rows.length) return { frame, issues: [], result: { added: 0 } }
        const existing = frame.record.details[form] || []
        return {
          frame: { ...frame, dirty: true, record: { ...frame.record, details: { ...frame.record.details, [form]: [...existing, ...rows] } } },
          issues: [], result: { added: rows.length },
        }
      } catch (err) {
        return { frame, issues: issuesFromError(err) }
      }
    }
    case 'recalc_lines': {
      if (!op.source) return { frame, issues: [] }
      const rows = frame.record.details[form] || []
      const linked = rows.map((r, i) => ({ r, i })).filter(x => !x.r._deleted && (x.r._link as { key?: string } | undefined)?.key)
      if (!linked.length) return { frame, issues: [] }
      const exec = (a: string, p: Record<string, unknown>) => env.rt.execute(env.base.obj_name, a, p)
      const api = lineEntryApi(exec, {
        formNo: form, headerValues: frame.record.header, txnId: frame.txn_id || undefined, lines: rows,
      })
      try {
        const res = await api.recalc(op.source, linked.map((x, seq) => ({
          _src_key: String((x.r._link as { key: string }).key), values: rowForEvent(x.r), changed: [], seq,
        })))
        const next = rows.slice()
        for (const got of res.rows || []) {
          const at = linked.find(x => (x.r._link as { key: string }).key === got._src_key)
          if (at) next[at.i] = { ...next[at.i], ...got.values, ...(next[at.i]._txn_id ? { _edited: true } : {}) }
        }
        return { frame: { ...frame, record: { ...frame.record, details: { ...frame.record.details, [form]: next } } }, issues: [] }
      } catch (err) {
        return { frame, issues: issuesFromError(err) }
      }
    }
    case 'run_action':
      if (!op.action) return { frame, issues: [issue('run_action names no action.', 'WIZARD_ACTION')] }
      return runAction(env, frame, op.action)
    case 'validate': {
      if (op.scope === 'tree') return { frame, issues: await validateTree(env, frame) }
      try {
        await executeValidate(env.rt, env.base.obj_name, env.fi.headerNo, frame.record.header, { txnId: frame.txn_id || undefined })
        return { frame, issues: [] }
      } catch (err) {
        return { frame, issues: issuesFromError(err) }
      }
    }
    case 'fetch_url':
      return fetchUrl(env, frame, op.step || env.stepId || '')
    case 'goto': {
      const r = routeOf(op.goto)
      return { frame, issues: [], route: r }
    }
    case 'message': {
      const msg = text(op.message, scope)
      if (msg) env.notify(op.kind === 'error' ? 'error' : op.kind === 'warning' ? 'warning' : op.kind === 'success' ? 'success' : 'info', msg)
      return { frame, issues: [] }
    }
    default:
      return { frame, issues: [issue(`Unknown operation "${String((op as WizardOp).op)}".`, 'WIZARD_OP')] }
  }
}

/** Run a `then` / `do` list in order; each waits for the previous. Stops at
 *  the first blocking issue or the first route (goto ends the list). */
export async function runOps(env: OpsEnv, frame: WizardFrame, ops: WizardOp[] | undefined,
  ctx: { pending?: PendingLine } = {}): Promise<OpResult> {
  let cur = frame
  let result: Row | undefined
  const issues: WizardIssue[] = []
  for (const op of ops || []) {
    const r = await runOp(env, cur, op, { pending: ctx.pending, result })
    cur = r.frame
    issues.push(...r.issues)
    if (r.result) result = r.result
    if (blocking(r.issues)) return { frame: cur, issues, result }
    if (r.route) return { frame: cur, issues, result, route: r.route }
  }
  return { frame: cur, issues, result }
}

/** Whether a then-list resolves the scanned line itself (so a scan's `line.*`
 *  sets are staged for it rather than written to the current line). */
export function stagesLine(ops: WizardOp[] | undefined): boolean {
  return (ops || []).some(o => o.op === 'match_line' || o.op === 'add_line')
}

/** Split scan / fetch sets into the ones to stage for a matched line and the
 *  ones to apply now. */
export function splitSets(env: Pick<OpsEnv, 'fi'>, sets: WizardSet[], stage: boolean, form: string):
  { now: WizardSet[]; pending: PendingLine } {
  if (!stage) return { now: sets, pending: {} }
  const now: WizardSet[] = []
  const pending: PendingLine = {}
  for (const s of sets) {
    const t = parseTarget(s.target, env.fi, form)
    if (t && t.scope === 'line') pending[s.target] = s.value
    else now.push(s)
  }
  return { now, pending }
}

export { blocking as hasBlocking, columnFormKey }
